import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'
import { cn } from '../utils/cn'
import { ACCENT_GLASS_FILL_STYLE } from './AccentGlassPill'
import { InlineSpinner } from './InlineSpinner'

export type ButtonVariant = 'primary' | 'secondary' | 'danger'
export type ButtonSize = 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  /**
   * Show a spinner and block further clicks while an async action runs.
   * Wire it to the mutation's `isPending` so the user gets immediate
   * feedback on any button whose action isn't instant.
   */
  readonly loading?: boolean
  readonly children: ReactNode
}

/** The button-sized spinner — `InlineSpinner` at 14px, kept as its own
 *  name because that is what the button's own `loading` prop renders.
 *  For an icon `<button>` reach for `InlineSpinner` directly and pass
 *  the icon's size; there is one spinner in the kit, not two. */
export function ButtonSpinner() {
  return <InlineSpinner size={14} />
}

/**
 * The app's action button. Its corner comes from `--radius-button`
 * (12px by default, in packages/shared/styles/tokens.css) rather than the
 * `rounded-*` scale: at the shared 16px `rounded-lg` a short button equals
 * a full pill, and corner radius is the signal that separates "do
 * something" from the fully-round filter pills (`SegmentBar`) and tab
 * track (`TabSwitch`) — "switch view / filter". An app that flattens its
 * radii onto one value re-points that token and the button follows;
 * everywhere else it stays 12px.
 *
 * `primary` = brand-accent fill for the one main action; `secondary` =
 * muted glass fill for everything alongside it; `danger` = outlined in
 * the alarm colour for anything that destroys work — cancelling a job,
 * voiding an invoice, deleting a record. `disabled` works on any of
 * them (faded, no hover, not clickable) — it's a state, not a variant.
 *
 * `danger` reads its colour from a custom property rather than a Tailwind
 * token, so it needs no per-app theme entry: where an app defines
 * `--color-critical-channels` it uses that, and everywhere else it falls
 * back to the accent — which in those apps is still the brand red, so
 * the button looks the same either way. `size` is `md` (topbar / inline CTAs) or `lg` (the
 * roomier modal + form footers). Compose a leading icon as the first
 * child: `<Button variant="primary"><Plus size={13} />New invoice</Button>`.
 */
const BASE =
  'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-[var(--radius-button,12px)] font-semibold whitespace-nowrap transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50'

const SIZE: Record<ButtonSize, string> = {
  md: 'px-3.5 py-1.5 text-[12.5px]',
  lg: 'px-4 py-2 text-[13px]',
}

// Primary fill comes from the shared ACCENT_GLASS_FILL_STYLE constant
// (single source of truth for the accent — a rebrand touches one token),
// so the class only carries text + hover. `hover:brightness-110` works
// on top of the inline fill, the way LoginPage's submit button does it.
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'text-white hover:brightness-110',
  secondary:
    'bg-glass-fill-chip border border-glass-border text-glass-text hover:bg-glass-fill disabled:hover:bg-glass-fill-chip',
  // Outlined at rest, not on hover: a destructive control has to look
  // like a control before it is touched.
  danger:
    'bg-transparent border text-[rgb(var(--btn-danger))] border-[rgb(var(--btn-danger)/0.35)] hover:bg-[rgb(var(--btn-danger)/0.1)] hover:border-[rgb(var(--btn-danger)/0.6)] disabled:hover:bg-transparent',
}

/** Resolved on the element so the arbitrary values above stay static. */
const DANGER_STYLE = {
  '--btn-danger': 'var(--color-critical-channels, var(--color-accent-channels))',
} as CSSProperties

export function Button({
  variant = 'primary',
  size = 'md',
  type,
  className,
  style,
  children,
  loading = false,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type ?? 'button'}
      // A loading button is also disabled so the action can't fire twice.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      style={
        variant === 'primary'
          ? { ...ACCENT_GLASS_FILL_STYLE, ...style }
          : variant === 'danger'
            ? { ...DANGER_STYLE, ...style }
            : style
      }
      className={cn(BASE, SIZE[size], VARIANT[variant], className)}
      {...rest}
    >
      {loading && <ButtonSpinner />}
      {children}
    </button>
  )
}
